// Signage → Redes sociales (/signage/social). Muro de posts para la escena
// `social` del reproductor. Dos modos por cuenta:
//   · curado — el staff carga los posts a mano (imagen de la biblioteca + texto).
//   · api    — el worker (notificador) sincroniza Instagram Business / Facebook
//     Page vía Graph API de Meta con un token cifrado (TikTok/X: solo curado).
// El staff puede FIJAR un post (siempre primero) u OCULTARLO sin borrarlo (la
// decisión sobrevive a los re-sync del worker).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, KeyRound, Pencil, Pin, PinOff, Plus, Share2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { REDES_SOCIALES, sigInsert, sigList, sigRpc, sigUpdate } from './shared';
import { MediaPicker } from './MediaPicker';
import { LOCALE } from '@/lib/utils/regional';

interface Cuenta {
  id: string; red: string; handle: string; nombre: string | null; modo: 'curado' | 'api';
  api_config: { ig_user_id?: string; page_id?: string }; token_expira: string | null;
  intervalo_min: number; max_posts: number; activo: boolean;
  ultima_sync: string | null; ultimo_error: string | null; token_cifrado?: string | null;
}
interface Post {
  id: string; cuenta_id: string; origen: 'curado' | 'api'; texto: string | null; imagen: string | null;
  likes: number | null; comentarios: number | null; publicado_en: string; fijado: boolean; activo: boolean;
}

const redLabel = (v: string) => REDES_SOCIALES.find((r) => r.v === v)?.l ?? v;

export function SignageSocial() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [editCta, setEditCta] = useState<Partial<Cuenta> | null>(null);
  const [tokenDe, setTokenDe] = useState<Cuenta | null>(null);
  const [token, setToken] = useState('');
  const [tokenExp, setTokenExp] = useState('');
  const [editPost, setEditPost] = useState<Partial<Post> | null>(null);
  const [pickImg, setPickImg] = useState(false);
  const [filtroCta, setFiltroCta] = useState('');
  const [guardando, setGuardando] = useState(false);

  const { data: cuentas = [] } = useQuery<Cuenta[]>({
    queryKey: ['sig-social-cuentas'],
    queryFn: () => sigList<Cuenta>('social_cuenta?select=*&deleted_at=is.null&order=red,handle'),
    staleTime: 0,
  });
  const { data: posts = [] } = useQuery<Post[]>({
    queryKey: ['sig-social-posts', filtroCta],
    queryFn: () => sigList<Post>(`social_post?select=*&deleted_at=is.null${filtroCta ? `&cuenta_id=eq.${filtroCta}` : ''}&order=fijado.desc,publicado_en.desc&limit=60`),
    staleTime: 0,
  });
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['sig-social-cuentas'] });
    void qc.invalidateQueries({ queryKey: ['sig-social-posts'] });
  };

  const guardarCuenta = async () => {
    if (!editCta?.red || !editCta.handle?.trim()) { toast.error('Red y usuario (handle) son obligatorios'); return; }
    setGuardando(true);
    try {
      const body = {
        red: editCta.red, handle: editCta.handle.trim().replace(/^@/, ''), nombre: editCta.nombre || null,
        modo: editCta.modo ?? 'curado',
        api_config: editCta.modo === 'api' ? (editCta.api_config ?? {}) : {},
        intervalo_min: editCta.intervalo_min ?? 60, max_posts: editCta.max_posts ?? 8, activo: editCta.activo ?? true,
      };
      if (editCta.id) await sigUpdate('social_cuenta', editCta.id, body);
      else await sigInsert('social_cuenta', body);
      setEditCta(null); invalidar();
      toast.success(body.modo === 'api' ? 'Cuenta guardada — recuerda cargar el token (🔑)' : 'Cuenta guardada');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const guardarToken = async () => {
    if (!tokenDe) return;
    setGuardando(true);
    try {
      await sigRpc('sig_social_token_set', { _cuenta: tokenDe.id, _token: token, _expira: tokenExp || null });
      setTokenDe(null); setToken(''); setTokenExp(''); invalidar();
      toast.success('Token guardado cifrado — el worker sincroniza en el próximo ciclo');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const guardarPost = async () => {
    if (!editPost?.cuenta_id) { toast.error('Elige la cuenta del post'); return; }
    if (!editPost.texto?.trim() && !editPost.imagen) { toast.error('El post necesita texto o imagen'); return; }
    setGuardando(true);
    try {
      const body = {
        cuenta_id: editPost.cuenta_id, origen: 'curado', texto: editPost.texto || null, imagen: editPost.imagen || null,
        likes: editPost.likes ?? null, comentarios: editPost.comentarios ?? null,
        publicado_en: editPost.publicado_en ?? new Date().toISOString(), fijado: editPost.fijado ?? false, activo: true,
      };
      if (editPost.id) await sigUpdate('social_post', editPost.id, body);
      else await sigInsert('social_post', body);
      setEditPost(null); invalidar(); toast.success('Post guardado');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const patchPost = (p: Post, patch: Record<string, unknown>) =>
    sigUpdate('social_post', p.id, patch).then(invalidar).catch((e) => toast.error(getErrorMessage(e)));

  const eliminarCuenta = async (c: Cuenta) => {
    if (!(await dlg.confirmar(`¿Eliminar la cuenta @${c.handle} y dejar de mostrar sus posts?`, { peligro: true }))) return;
    void sigUpdate('social_cuenta', c.id, { deleted_at: new Date().toISOString(), activo: false })
      .then(invalidar).then(() => toast.success('Cuenta eliminada')).catch((e) => toast.error(getErrorMessage(e)));
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Share2 className="w-5 h-5" /> Redes sociales</h1>
        <Button className="ml-auto" variant="outline" onClick={() => setEditCta({ red: 'instagram', modo: 'curado', intervalo_min: 60, max_posts: 8, activo: true })}><Plus className="w-4 h-4 mr-1" /> Cuenta</Button>
        <Button onClick={() => setEditPost({ cuenta_id: cuentas[0]?.id, fijado: false })} disabled={!cuentas.length}><Plus className="w-4 h-4 mr-1" /> Post curado</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Agrega la escena <strong>Redes sociales</strong> a una plantilla y las pantallas rotan estos posts.
        Modo <strong>curado</strong>: cargas los posts a mano (control total de lo exhibido). Modo <strong>API</strong>:
        el worker sincroniza Instagram Business/Facebook Page con un token de Meta (cifrado); TikTok y X por ahora solo curado.
      </p>

      {/* Cuentas */}
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Cuenta</th>
            <th className="py-2 px-3 text-center">Modo</th><th className="py-2 px-3 text-left">Última sync</th>
            <th className="py-2 px-3 text-center">Posts</th><th className="py-2 px-3 text-center">Activa</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {cuentas.map((c, i) => (
              <tr key={c.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">
                  <span className="font-medium">{redLabel(c.red)} · @{c.handle}</span>
                  {c.nombre && <span className="text-muted-foreground"> — {c.nombre}</span>}
                  {c.ultimo_error && <div className="text-[11px] text-amber-600 dark:text-amber-400" title={c.ultimo_error}>⚠ {c.ultimo_error.slice(0, 70)}</div>}
                </td>
                <td className="py-2 px-3 text-center">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${c.modo === 'api' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-muted text-muted-foreground'}`}>
                    {c.modo === 'api' ? 'API' : 'Curado'}
                  </span>
                </td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{c.modo === 'api' ? (c.ultima_sync ? new Date(c.ultima_sync).toLocaleString(LOCALE) : 'pendiente') : '—'}</td>
                <td className="py-2 px-3 text-center tabular-nums">{posts.filter((p) => p.cuenta_id === c.id).length}</td>
                <td className="py-2 px-3 text-center">{c.activo ? 'Sí' : 'No'}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  {c.modo === 'api' && (
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={c.token_cifrado ? 'Token cargado — reemplazar' : 'Cargar token de la API'}
                      onClick={() => { setTokenDe(c); setToken(''); setTokenExp(c.token_expira ?? ''); }}>
                      <KeyRound className={`w-4 h-4 ${c.token_cifrado ? 'text-green-600' : 'text-amber-500'}`} />
                    </button>
                  )}
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEditCta(c)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => eliminarCuenta(c)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!cuentas.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin cuentas. Crea la primera (p. ej. Instagram @tuempresa).</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Posts */}
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">Posts</h2>
        <select className={`${fieldCls} w-auto text-xs ml-auto`} value={filtroCta} onChange={(e) => setFiltroCta(e.target.value)}>
          <option value="">Todas las cuentas</option>
          {cuentas.map((c) => <option key={c.id} value={c.id}>{redLabel(c.red)} · @{c.handle}</option>)}
        </select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((p) => {
          const c = cuentas.find((x) => x.id === p.cuenta_id);
          return (
            <div key={p.id} className={`border rounded-lg overflow-hidden ${p.activo ? '' : 'opacity-50'}`}>
              {p.imagen
                ? <img src={p.imagen} alt="" className="w-full h-32 object-cover" />
                : <div className="w-full h-32 bg-muted flex items-center justify-center text-muted-foreground"><ImageIcon className="w-6 h-6" /></div>}
              <div className="p-2.5 space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium truncate">{c ? `@${c.handle}` : '—'}</span>
                  <span className="text-muted-foreground">{redLabel(c?.red ?? '')}</span>
                  <span className={`ml-auto rounded px-1.5 py-0.5 text-[10px] uppercase ${p.origen === 'api' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-muted text-muted-foreground'}`}>{p.origen}</span>
                </div>
                {p.texto && <p className="line-clamp-2 text-muted-foreground">{p.texto}</p>}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span>{new Date(p.publicado_en).toLocaleDateString(LOCALE)}</span>
                  {p.likes != null && <span>♥ {p.likes}</span>}
                  <span className="ml-auto flex items-center gap-0.5">
                    <button className="inline-flex w-6 h-6 items-center justify-center rounded hover:bg-muted" title={p.fijado ? 'Quitar fijado' : 'Fijar (siempre primero)'}
                      onClick={() => void patchPost(p, { fijado: !p.fijado })}>
                      {p.fijado ? <Pin className="w-3.5 h-3.5 text-primary" /> : <PinOff className="w-3.5 h-3.5" />}
                    </button>
                    <Switch checked={p.activo} onChange={(v) => void patchPost(p, { activo: v })} label="" />
                    {p.origen === 'curado' && (
                      <button className="inline-flex w-6 h-6 items-center justify-center rounded hover:bg-muted" title="Editar" onClick={() => setEditPost(p)}><Pencil className="w-3.5 h-3.5" /></button>
                    )}
                    <button className="inline-flex w-6 h-6 items-center justify-center rounded hover:bg-muted" title="Eliminar"
                      onClick={() => void patchPost(p, { deleted_at: new Date().toISOString() })}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        {!posts.length && <div className="col-span-full py-8 text-center text-sm text-muted-foreground">Sin posts todavía. Crea uno curado o configura una cuenta en modo API.</div>}
      </div>

      {/* Modal cuenta */}
      {editCta && (
        <Modal title={editCta.id ? 'Editar cuenta' : 'Nueva cuenta social'} onClose={() => setEditCta(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Red">
                <select className={fieldCls} value={editCta.red ?? 'instagram'} onChange={(e) => setEditCta({ ...editCta, red: e.target.value })}>
                  {REDES_SOCIALES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
              </Campo>
              <Campo label="Usuario (handle)"><input className={fieldCls} placeholder="miempresa" value={editCta.handle ?? ''} onChange={(e) => setEditCta({ ...editCta, handle: e.target.value })} /></Campo>
            </div>
            <Campo label="Nombre a mostrar (opcional)"><input className={fieldCls} value={editCta.nombre ?? ''} onChange={(e) => setEditCta({ ...editCta, nombre: e.target.value })} /></Campo>
            <Campo label="Modo">
              <select className={fieldCls} value={editCta.modo ?? 'curado'} onChange={(e) => setEditCta({ ...editCta, modo: e.target.value as Cuenta['modo'] })}>
                <option value="curado">Curado — cargo los posts a mano</option>
                <option value="api" disabled={editCta.red === 'tiktok' || editCta.red === 'x'}>API — sincronización automática (Instagram/Facebook)</option>
              </select>
            </Campo>
            {editCta.modo === 'api' && (
              <>
                {editCta.red === 'instagram' && (
                  <Campo label="IG User ID (cuenta Business; vacío si usas token de Instagram Login)">
                    <input className={fieldCls} value={editCta.api_config?.ig_user_id ?? ''} onChange={(e) => setEditCta({ ...editCta, api_config: { ...editCta.api_config, ig_user_id: e.target.value } })} />
                  </Campo>
                )}
                {editCta.red === 'facebook' && (
                  <Campo label="Page ID (página de Facebook)">
                    <input className={fieldCls} value={editCta.api_config?.page_id ?? ''} onChange={(e) => setEditCta({ ...editCta, api_config: { ...editCta.api_config, page_id: e.target.value } })} />
                  </Campo>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Sincronizar cada (min)"><input type="number" min={15} max={1440} className={fieldCls} value={editCta.intervalo_min ?? 60} onChange={(e) => setEditCta({ ...editCta, intervalo_min: Number(e.target.value) })} /></Campo>
                  <Campo label="Máx. posts"><input type="number" min={1} max={20} className={fieldCls} value={editCta.max_posts ?? 8} onChange={(e) => setEditCta({ ...editCta, max_posts: Number(e.target.value) })} /></Campo>
                </div>
                <p className="text-xs text-muted-foreground">El token de acceso se carga aparte con el botón 🔑 de la lista (queda cifrado en la base).</p>
              </>
            )}
            <Switch checked={editCta.activo ?? true} onChange={(v) => setEditCta({ ...editCta, activo: v })} label="Cuenta activa" />
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEditCta(null)}>Cancelar</Button><Button onClick={() => void guardarCuenta()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}

      {/* Modal token */}
      {tokenDe && (
        <Modal title={`Token de ${redLabel(tokenDe.red)} · @${tokenDe.handle}`} onClose={() => setTokenDe(null)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Token de acceso de <strong>larga duración</strong> de Meta (≈60 días). Se guarda <strong>cifrado</strong> y solo el
              worker lo usa. Se obtiene en Meta Developers con la cuenta Business/página vinculada.
            </p>
            <Campo label="Token de acceso"><textarea className={`${fieldCls} h-20 font-mono text-xs`} value={token} onChange={(e) => setToken(e.target.value)} placeholder="EAAG…" /></Campo>
            <Campo label="Vence el (opcional — para avisarte antes)"><input type="date" className={fieldCls} value={tokenExp} onChange={(e) => setTokenExp(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setTokenDe(null)}>Cancelar</Button><Button onClick={() => void guardarToken()} isLoading={guardando} disabled={!token.trim()}>Guardar token</Button></div>
          </div>
        </Modal>
      )}

      {/* Modal post curado */}
      {editPost && (
        <Modal title={editPost.id ? 'Editar post' : 'Nuevo post curado'} onClose={() => setEditPost(null)}>
          <div className="space-y-3">
            <Campo label="Cuenta">
              <select className={fieldCls} value={editPost.cuenta_id ?? ''} onChange={(e) => setEditPost({ ...editPost, cuenta_id: e.target.value })}>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{redLabel(c.red)} · @{c.handle}</option>)}
              </select>
            </Campo>
            <Campo label="Texto del post">
              <textarea className={`${fieldCls} h-20`} value={editPost.texto ?? ''} onChange={(e) => setEditPost({ ...editPost, texto: e.target.value })} />
            </Campo>
            <div className="flex items-center gap-3">
              {editPost.imagen
                ? <img src={editPost.imagen} alt="" className="w-20 h-20 object-cover rounded-md border" />
                : <div className="w-20 h-20 rounded-md border bg-muted flex items-center justify-center text-muted-foreground"><ImageIcon className="w-5 h-5" /></div>}
              <div className="space-x-2">
                <Button size="sm" variant="outline" onClick={() => setPickImg(true)}>Elegir de la biblioteca…</Button>
                {editPost.imagen && <Button size="sm" variant="ghost" onClick={() => setEditPost({ ...editPost, imagen: null })}>Quitar</Button>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Likes (opcional)"><input type="number" min={0} className={fieldCls} value={editPost.likes ?? ''} onChange={(e) => setEditPost({ ...editPost, likes: e.target.value === '' ? null : Number(e.target.value) })} /></Campo>
              <Campo label="Comentarios"><input type="number" min={0} className={fieldCls} value={editPost.comentarios ?? ''} onChange={(e) => setEditPost({ ...editPost, comentarios: e.target.value === '' ? null : Number(e.target.value) })} /></Campo>
              <Campo label="Fecha"><input type="date" className={fieldCls} value={(editPost.publicado_en ?? '').slice(0, 10)} onChange={(e) => setEditPost({ ...editPost, publicado_en: e.target.value ? new Date(`${e.target.value}T12:00:00`).toISOString() : undefined })} /></Campo>
            </div>
            <Switch checked={editPost.fijado ?? false} onChange={(v) => setEditPost({ ...editPost, fijado: v })} label="Fijado (siempre primero)" />
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEditPost(null)}>Cancelar</Button><Button onClick={() => void guardarPost()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}

      {pickImg && (
        <MediaPicker tipo="imagen" onClose={() => setPickImg(false)}
          onPick={(m) => { setEditPost((p) => ({ ...p, imagen: m.datos ?? m.url ?? null })); setPickImg(false); }} />
      )}
    </div>
  );
}
